import { startCase } from 'es-toolkit';
import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { Lumber, LumberFilter, MemberType } from '@paragon/contract-library';
import { DistanceFilterField } from '@paragon/material';
import { applyLumberPatchToLumberLikeObject, LumberSelects } from '@paragon/material-lists';
import { omitBy } from '@paragon/utilities';

interface Props {
  readonly sx?: SxProps<Theme>;

  readonly lumbers: readonly Lumber[];

  readonly value: LumberFilter;
  readonly onChange: (value: LumberFilter) => void;
}

const PLACEHOLDER = 'Any' as const;

// https://github.com/ParagonTruss/ParagonCore/pull/3547#discussion_r362596232
// if you ever change this code to not use a whitelist, remove the comment on the server's LumberFilter.cs
const MEMBER_TYPES: readonly MemberType[] = [
  'TopChord',
  'BottomChord',
  'Web',
  'EndVertical',
  'Slider',
  'Wedge',
  'SpliceBlock',
  'BearingBlock',
  'NonstructuralBlock',
  'GableStud',
  'ExtraplanarBrace',
  'Scab',
  'Unknown',
];

export function LumberWhereFilter(props: Props) {
  function onChange(changes: Partial<LumberFilter>) {
    props.onChange(omitBy({ ...props.value, ...changes }, (value) => value == null));
  }

  return (
    <Box sx={[{ display: 'flex', flexDirection: 'column' }, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}>
      <Box sx={{ display: 'flex' }}>
        <PropertySelectField
          sx={{ m: 1, flex: 1 }}
          property="Member Type"
          options={[PLACEHOLDER, ...MEMBER_TYPES]}
          getName={startCase}
          value={props.value.memberType ?? PLACEHOLDER}
          submit={(memberType) => {
            onChange({ memberType: memberType === PLACEHOLDER ? undefined : (memberType ?? undefined) });
          }}
        />
        <DistanceFilterField
          sx={{ m: 1, flex: 1 }}
          label="Length"
          placeholder={PLACEHOLDER}
          canBeNull
          value={props.value.length ?? null}
          onSubmit={(length) => {
            onChange({ length: length ?? undefined });
          }}
        />
      </Box>

      <Box
        sx={{
          margin: 1,

          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',

          '& > *': {
            margin: 0,
            width: 'unset',
          },
        }}
      >
        <LumberSelects
          inputSx={{ m: 1, flex: 1 }}
          placeholder={PLACEHOLDER}
          lumbers={props.lumbers}
          value={props.value}
          onChange={(patch) => {
            props.onChange(applyLumberPatchToLumberLikeObject(props.value!, patch));
          }}
        />
      </Box>
    </Box>
  );
}
